import { useSignal } from "@preact/signals";
import type { JSX } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { rpc } from "../../rpc";
import {
  getCachedHoverCard,
  type HoverCardData,
  setCachedHoverCard,
} from "../../stores/hover-card-cache";
import { IcoChevronRight, IcoMail, IcoUser, IcoUsers } from "../icons";

type MemberHoverCardProps = {
  memberId: string;
  workspaceId: string;
  children: JSX.Element;
  class?: string;
};

const SHOW_DELAY = 300;
const HIDE_DELAY = 150;
const CARD_WIDTH = 288; // w-72
const CARD_ESTIMATED_HEIGHT = 200;

export function MemberHoverCard({
  memberId,
  workspaceId,
  children,
  class: className,
}: MemberHoverCardProps) {
  const isVisible = useSignal(false);
  const data = useSignal<HoverCardData | null>(null);
  const loading = useSignal(false);
  const cardPosition = useSignal<{
    top: number;
    left: number;
    flipAbove: boolean;
  } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const fetchData = () => {
    const cached = getCachedHoverCard(memberId);
    if (cached) {
      data.value = cached;
      return;
    }
    if (loading.value) return;
    loading.value = true;
    rpc.members
      .getHoverCard({ workspaceId, memberId })
      .then((result) => {
        data.value = result;
        setCachedHoverCard(result);
        loading.value = false;
      })
      .catch(() => {
        loading.value = false;
      });
  };

  const computePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipAbove =
      spaceBelow < CARD_ESTIMATED_HEIGHT && rect.top > spaceBelow;

    let left = rect.left;
    if (left + CARD_WIDTH > window.innerWidth - 16) {
      left = window.innerWidth - CARD_WIDTH - 16;
    }
    if (left < 16) left = 16;

    const top = flipAbove ? rect.top - 8 : rect.bottom + 8;
    cardPosition.value = { top, left, flipAbove };
  };

  const startShow = () => {
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      computePosition();
      isVisible.value = true;
      fetchData();
    }, SHOW_DELAY);
  };

  const startHide = () => {
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      isVisible.value = false;
    }, HIDE_DELAY);
  };

  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  // Close on scroll
  useEffect(() => {
    if (!isVisible.value) return;
    const handleScroll = () => {
      isVisible.value = false;
      clearTimers();
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isVisible.value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimers();
  }, []);

  const profileUrl = `/orgs/${workspaceId}/members/${memberId}`;

  return (
    <span
      ref={triggerRef}
      class={className ?? "inline"}
      onMouseEnter={startShow}
      onMouseLeave={startHide}
    >
      {children}
      {isVisible.value && cardPosition.value && (
        <div
          class="fixed z-[60] w-72 bg-surface border border-border rounded-lg shadow-lg animate-scale-in"
          style={{
            top: cardPosition.value.flipAbove
              ? undefined
              : `${cardPosition.value.top}px`,
            bottom: cardPosition.value.flipAbove
              ? `${window.innerHeight - cardPosition.value.top}px`
              : undefined,
            left: `${cardPosition.value.left}px`,
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={startHide}
        >
          {loading.value && !data.value ? (
            <SkeletonCard />
          ) : data.value ? (
            <CardContent data={data.value} profileUrl={profileUrl} />
          ) : null}
        </div>
      )}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div class="p-4 space-y-3">
      <div class="flex items-center gap-3">
        <div class="size-12 rounded-full bg-surface-muted animate-pulse" />
        <div class="space-y-1.5 flex-1">
          <div class="h-4 w-28 bg-surface-muted rounded animate-pulse" />
          <div class="h-3 w-20 bg-surface-muted rounded animate-pulse" />
        </div>
      </div>
      <div class="border-t border-border pt-3 space-y-2">
        <div class="h-3 w-40 bg-surface-muted rounded animate-pulse" />
        <div class="h-3 w-36 bg-surface-muted rounded animate-pulse" />
      </div>
    </div>
  );
}

function CardContent({
  data,
  profileUrl,
}: {
  data: HoverCardData;
  profileUrl: string;
}) {
  const imageError = useSignal(false);
  const initial = data.name?.charAt(0)?.toUpperCase() ?? "?";

  return (
    <div>
      {/* Header */}
      <div class="p-4 flex items-center gap-3">
        {data.profilePictureUrl && !imageError.value ? (
          <img
            src={data.profilePictureUrl}
            alt=""
            class="size-12 rounded-full object-cover flex-shrink-0"
            onError={() => {
              imageError.value = true;
            }}
          />
        ) : (
          <div class="size-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <span class="text-lg font-medium text-amber-800 dark:text-amber-200">
              {initial}
            </span>
          </div>
        )}
        <div class="min-w-0">
          <div class="font-semibold text-text text-sm truncate">
            {data.name}
          </div>
          {data.title && (
            <div class="text-xs text-text-muted truncate">{data.title}</div>
          )}
        </div>
      </div>

      {/* Details */}
      <div class="border-t border-border px-4 py-3 space-y-2 text-xs text-text-muted">
        <div class="flex items-center gap-2">
          <IcoMail class="size-3.5 flex-shrink-0" />
          <span class="truncate">{data.email}</span>
        </div>
        {data.managerName && (
          <div class="flex items-center gap-2">
            <IcoUser class="size-3.5 flex-shrink-0" />
            <span class="truncate">Reports to {data.managerName}</span>
          </div>
        )}
        {data.directReportCount > 0 && (
          <div class="flex items-center gap-2">
            <IcoUsers class="size-3.5 flex-shrink-0" />
            <span>
              {data.directReportCount} direct report
              {data.directReportCount !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* View profile link */}
      <div class="border-t border-border">
        <a
          href={profileUrl}
          class="flex items-center justify-between px-4 py-2.5 text-xs font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors rounded-b-lg"
        >
          View profile
          <IcoChevronRight class="size-3.5" />
        </a>
      </div>
    </div>
  );
}
